import { useState, useEffect } from 'react';
import { 
  Settings, 
  User, 
  Bell, 
  Palette, 
  Shield, 
  HelpCircle,
  ChevronRight,
  Moon,
  Sun,
  Globe,
  Trash2,
  Download,
  LogOut,
  Crown,
  Sparkles,
  X,
  Loader2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

interface SettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  isNightMode: boolean;
  onToggleNightMode: () => void;
}

interface NotificationPreferences {
  daily_outfit: boolean;
  weather_alerts: boolean;
  style_tips: boolean;
  social_activity: boolean;
  packing_reminders: boolean;
}

export function SettingsModal({ isOpen, onClose, isNightMode, onToggleNightMode }: SettingsModalProps) {
  const { user, profile, signOut, updateProfile } = useAuth();
  const [activeSection, setActiveSection] = useState<string | null>(null);
  const [displayName, setDisplayName] = useState(profile?.display_name || '');
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  const [savingNotifications, setSavingNotifications] = useState(false);
  const [exportingData, setExportingData] = useState(false);
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [notifications, setNotifications] = useState<NotificationPreferences>({
    daily_outfit: true,
    weather_alerts: true,
    style_tips: true,
    social_activity: true,
    packing_reminders: true,
  });

  // Load notification preferences from database
  useEffect(() => {
    if (isOpen && user && activeSection === 'notifications') {
      loadNotificationPreferences();
    }
  }, [isOpen, user, activeSection]);

  const loadNotificationPreferences = async () => {
    if (!user) return;
    setLoadingNotifications(true);
    try {
      const { data, error } = await supabase
        .from('notification_preferences')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (data) {
        setNotifications({
          daily_outfit: data.daily_outfit ?? true,
          weather_alerts: data.weather_alerts ?? true,
          style_tips: data.style_tips ?? true,
          social_activity: data.social_activity ?? true,
          packing_reminders: data.packing_reminders ?? true,
        });
      } else if (error?.code === 'PGRST116') {
        // No record exists, create one with defaults
        await supabase.from('notification_preferences').insert({
          user_id: user.id,
          daily_outfit: true,
          weather_alerts: true,
          style_tips: true,
          social_activity: true,
          packing_reminders: true,
        });
      }
    } catch (error) {
      console.error('Error loading notification preferences:', error);
    } finally {
      setLoadingNotifications(false);
    }
  };

  const handleNotificationChange = async (key: keyof NotificationPreferences, value: boolean) => {
    if (!user) return;
    
    const newNotifications = { ...notifications, [key]: value };
    setNotifications(newNotifications);
    setSavingNotifications(true);
    
    try {
      const { error } = await supabase
        .from('notification_preferences')
        .upsert({
          user_id: user.id,
          [key]: value,
          updated_at: new Date().toISOString(),
        }, { onConflict: 'user_id' });

      if (error) throw error;
    } catch (error) {
      console.error('Error saving notification preference:', error);
      // Revert on error
      setNotifications(prev => ({ ...prev, [key]: !value }));
      toast.error('Failed to save preference');
    } finally {
      setSavingNotifications(false);
    }
  };

  if (!isOpen) return null;

  const handleSaveProfile = async () => {
    if (updateProfile) {
      await updateProfile({ display_name: displayName });
      toast.success('Profile updated');
      setActiveSection(null);
    }
  };

  const handleExportData = async () => {
    if (!user) return;
    setExportingData(true);
    
    try {
      const { data, error } = await supabase.functions.invoke('export-user-data');
      
      if (error) throw error;
      
      // Create and download the JSON file
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `aura-data-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success('Your data has been exported');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export data. Please try again.');
    } finally {
      setExportingData(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!user) return;
    
    const confirmed = window.confirm(
      'Are you sure you want to delete your account? This action is irreversible and all your data will be permanently deleted.'
    );
    
    if (!confirmed) return;
    
    const doubleConfirmed = window.confirm(
      'This is your final warning. Your wardrobe, outfits, and all associated data will be permanently deleted. Continue?'
    );
    
    if (!doubleConfirmed) return;
    
    setDeletingAccount(true);
    
    try {
      const { error } = await supabase.functions.invoke('delete-account');
      
      if (error) throw error;
      
      toast.success('Your account has been deleted');
      await signOut();
    } catch (error) {
      console.error('Delete account error:', error);
      toast.error('Failed to delete account. Please contact support.');
    } finally {
      setDeletingAccount(false);
    }
  };

  const handleContactSupport = () => {
    window.location.href = 'mailto:support@aura.style?subject=AURA Support Request';
  };

  const sections = [
    { 
      id: 'profile', 
      icon: User, 
      label: 'Profile Settings',
      description: 'Manage your personal information'
    },
    { 
      id: 'appearance', 
      icon: Palette, 
      label: 'Appearance',
      description: 'Customize how AURA looks'
    },
    { 
      id: 'notifications', 
      icon: Bell, 
      label: 'Notifications',
      description: 'Control your alerts and updates'
    },
    { 
      id: 'privacy', 
      icon: Shield, 
      label: 'Privacy & Security',
      description: 'Manage your data and security'
    },
    { 
      id: 'help', 
      icon: HelpCircle, 
      label: 'Help & Support',
      description: 'Get help with AURA'
    },
  ];

  const notificationLabels: Record<keyof NotificationPreferences, { title: string; description: string }> = {
    daily_outfit: { title: 'Daily Outfit', description: 'Morning outfit suggestions' },
    weather_alerts: { title: 'Weather Alerts', description: 'Outfit changes for weather' },
    style_tips: { title: 'Style Tips', description: 'Personalized fashion advice' },
    social_activity: { title: 'Social Activity', description: 'Likes, comments, follows' },
    packing_reminders: { title: 'Packing Reminders', description: 'Trip packing notifications' },
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-background/80 backdrop-blur-sm p-4">
      <div className="w-full max-w-lg max-h-[90vh] overflow-hidden relative rounded-3xl shadow-2xl bg-card border border-border">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center gap-3">
            {activeSection && (
              <button 
                onClick={() => setActiveSection(null)}
                className="p-1 hover:bg-secondary rounded-lg transition-colors"
              >
                <ChevronRight size={20} className="rotate-180" />
              </button>
            )}
            <div>
              <h3 className="text-xl font-display">
                {activeSection ? sections.find(s => s.id === activeSection)?.label : 'Settings'}
              </h3>
              {!activeSection && (
                <p className="text-xs text-muted-foreground mt-0.5">Customize your AURA experience</p>
              )}
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-secondary rounded-lg transition-colors">
            <X size={20} />
          </button>
        </div>

        <div className="overflow-y-auto max-h-[calc(90vh-80px)]">
          {/* Main Menu */}
          {!activeSection && (
            <div className="p-4">
              {/* Premium Banner */}
              {!profile?.is_premium && (
                <div className="p-4 mb-4 rounded-xl bg-gradient-to-r from-aura-gold/20 to-aura-gold/5 border border-aura-gold/20">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-aura-gold/20 flex items-center justify-center">
                      <Crown size={20} className="text-aura-gold" />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-sm">Upgrade to AURA+</p>
                      <p className="text-xs text-muted-foreground">Unlock all premium features</p>
                    </div>
                    <Button size="sm" className="btn-aura text-xs">
                      Upgrade
                    </Button>
                  </div>
                </div>
              )}

              {/* Sections */}
              <div className="space-y-1">
                {sections.map((section) => (
                  <button
                    key={section.id}
                    onClick={() => setActiveSection(section.id)}
                    className="w-full flex items-center gap-4 p-4 rounded-xl hover:bg-secondary/50 transition-colors"
                  >
                    <div className="w-10 h-10 rounded-lg bg-secondary flex items-center justify-center">
                      <section.icon size={18} className="text-muted-foreground" />
                    </div>
                    <div className="flex-1 text-left">
                      <p className="text-sm font-medium">{section.label}</p>
                      <p className="text-xs text-muted-foreground">{section.description}</p>
                    </div>
                    <ChevronRight size={16} className="text-muted-foreground" />
                  </button>
                ))}
              </div>

              {/* Sign Out */}
              <button
                onClick={signOut}
                className="w-full flex items-center gap-4 p-4 mt-4 rounded-xl hover:bg-destructive/10 transition-colors text-destructive"
              >
                <div className="w-10 h-10 rounded-lg bg-destructive/10 flex items-center justify-center">
                  <LogOut size={18} />
                </div>
                <div className="flex-1 text-left">
                  <p className="text-sm font-medium">Sign Out</p>
                  <p className="text-xs opacity-70">{user?.email}</p>
                </div>
              </button>
            </div>
          )}

          {/* Profile Section */}
          {activeSection === 'profile' && (
            <div className="p-6 space-y-6">
              <div>
                <Label className="text-xs uppercase tracking-widest">Display Name</Label>
                <Input
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="Your display name"
                  className="mt-2"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest">Email</Label>
                <Input value={user?.email || ''} disabled className="mt-2 opacity-60" />
                <p className="text-xs text-muted-foreground mt-1">Email cannot be changed</p>
              </div>
              <Button onClick={handleSaveProfile} className="w-full btn-aura">
                Save Changes
              </Button>
            </div>
          )}

          {/* Appearance Section */}
          {activeSection === 'appearance' && (
            <div className="p-6 space-y-6">
              <div className="flex items-center justify-between p-4 rounded-xl bg-secondary/30">
                <div className="flex items-center gap-3">
                  {isNightMode ? <Moon size={20} /> : <Sun size={20} />}
                  <div>
                    <p className="text-sm font-medium">Night Mode</p>
                    <p className="text-xs text-muted-foreground">Darker theme for evening</p>
                  </div>
                </div>
                <Switch checked={isNightMode} onCheckedChange={onToggleNightMode} />
              </div>

              <div className="flex items-center justify-between p-4 rounded-xl bg-secondary/30 opacity-60">
                <div className="flex items-center gap-3">
                  <Globe size={20} />
                  <div>
                    <p className="text-sm font-medium">Language</p>
                    <p className="text-xs text-muted-foreground">English (US)</p>
                  </div>
                </div>
                <span className="text-xs text-muted-foreground">Coming soon</span>
              </div>
            </div>
          )}

          {/* Notifications Section */}
          {activeSection === 'notifications' && (
            <div className="p-6 space-y-4">
              {loadingNotifications ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="animate-spin text-muted-foreground" size={24} />
                </div>
              ) : (
                <>
                  {(Object.keys(notifications) as Array<keyof NotificationPreferences>).map((key) => (
                    <div key={key} className="flex items-center justify-between p-4 rounded-xl bg-secondary/30">
                      <div>
                        <p className="text-sm font-medium">{notificationLabels[key].title}</p>
                        <p className="text-xs text-muted-foreground">{notificationLabels[key].description}</p>
                      </div>
                      <Switch 
                        checked={notifications[key]} 
                        onCheckedChange={(checked) => handleNotificationChange(key, checked)}
                        disabled={savingNotifications}
                      />
                    </div>
                  ))}
                </>
              )}
            </div>
          )}

          {/* Privacy Section */}
          {activeSection === 'privacy' && (
            <div className="p-6 space-y-4">
              <button
                onClick={handleExportData}
                disabled={exportingData}
                className="w-full flex items-center gap-4 p-4 rounded-xl bg-secondary/30 hover:bg-secondary/50 transition-colors disabled:opacity-50"
              >
                {exportingData ? (
                  <Loader2 size={20} className="animate-spin" />
                ) : (
                  <Download size={20} />
                )}
                <div className="flex-1 text-left">
                  <p className="text-sm font-medium">Export Your Data</p>
                  <p className="text-xs text-muted-foreground">Download all your AURA data</p>
                </div>
              </button>
              
              <button
                onClick={handleDeleteAccount}
                disabled={deletingAccount}
                className="w-full flex items-center gap-4 p-4 rounded-xl bg-destructive/10 hover:bg-destructive/20 transition-colors text-destructive disabled:opacity-50"
              >
                {deletingAccount ? (
                  <Loader2 size={20} className="animate-spin" />
                ) : (
                  <Trash2 size={20} />
                )}
                <div className="flex-1 text-left">
                  <p className="text-sm font-medium">Delete Account</p>
                  <p className="text-xs opacity-70">Permanently remove your account</p>
                </div>
              </button>
            </div>
          )}

          {/* Help Section */}
          {activeSection === 'help' && (
            <div className="p-6 space-y-4">
              <div className="p-4 rounded-xl bg-secondary/30">
                <p className="text-sm font-medium mb-2">Contact Support</p>
                <p className="text-xs text-muted-foreground mb-3">
                  Need help? Our team is here for you.
                </p>
                <Button variant="outline" size="sm" className="w-full" onClick={handleContactSupport}>
                  Send Message
                </Button>
              </div>
              
              <div className="p-4 rounded-xl bg-secondary/30">
                <p className="text-sm font-medium mb-2">App Version</p>
                <p className="text-xs text-muted-foreground">AURA v1.0.0</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
